// useKidStepNarration — ambient pet-voice narration of cook step
// transitions for any food the kid asked for. Mounted at the
// KidModeShell level so narration fires regardless of which tab
// the kid is on (coloring, watching the pet, exploring Foods, etc.).
//
// Logic:
//   1. Track kid's active food requests that have a cook_session_id
//      (i.e. the parent has started cooking). useMyKidRequests
//      already exposes these via realtime.
//   2. For each linked session, subscribe to cook_session_steps and
//      fire narrateKidStep on every new step open (started_at set,
//      finished_at null).
//   3. Dedup by step_id so we narrate each transition once.
//
// Per-session realtime sub: low overhead per kid (usually 0–2
// concurrent linked sessions), no global table sub.

import { useEffect, useRef } from "react";
import { supabase, safeChannel } from "./supabase";
import { useMyKidRequests } from "./useMyKidRequests";
import { useKidPetState } from "./useKidPetState";
import { narrateKidStep } from "./narrateKidStep";

export function useKidStepNarration(kidUserId) {
  const { rows } = useMyKidRequests(kidUserId);
  const { pet } = useKidPetState(kidUserId);
  const announcedRef = useRef(new Set()); // step ids we've already narrated
  const channelsRef = useRef(new Map());   // session_id → realtime channel

  // Active session ids (requests with cook_session_id set). Recompute
  // whenever rows change so we add subs for newly-linked sessions and
  // drop subs for sessions that finished / were dismissed.
  useEffect(() => {
    if (!kidUserId || !pet?.pet_slug) return;
    const activeSessions = rows
      .filter((r) => r.cook_session_id && r.displayState === "cooking")
      .map((r) => ({
        sessionId: r.cook_session_id,
        foodSlug:  r.recipe_slug || r.canonical_id,
        targetKind: r.request_kind === "recipe" ? "recipe" : "canonical",
        foodName:  r.food_name || r.recipe_slug || r.canonical_id,
      }))
      .filter((s) => s.sessionId && s.foodSlug);

    const activeIds = new Set(activeSessions.map((s) => s.sessionId));

    // Drop subscriptions for sessions no longer active.
    for (const [sid, ch] of channelsRef.current.entries()) {
      if (!activeIds.has(sid)) {
        try { supabase.removeChannel(ch); } catch { /* noop */ }
        channelsRef.current.delete(sid);
      }
    }

    // Add subscriptions for newly-active sessions.
    for (const s of activeSessions) {
      if (channelsRef.current.has(s.sessionId)) continue;

      // Initial backfill — any steps that opened BEFORE we subscribed
      // should still narrate when the kid lands in the app. Fire-and-
      // forget; the realtime sub takes over from here.
      (async () => {
        const { data } = await supabase
          .from("cook_session_steps")
          .select("id, step_id, step_title, started_at, finished_at")
          .eq("cook_session_id", s.sessionId)
          .is("finished_at", null)
          .order("started_at", { ascending: true });
        for (const row of (data || [])) {
          const key = `${s.sessionId}::${row.step_id || row.id}`;
          if (announcedRef.current.has(key)) continue;
          announcedRef.current.add(key);
          narrateKidStep({
            foodSlug:   s.foodSlug,
            foodName:   s.foodName,
            targetKind: s.targetKind,
            petSlug:    pet.pet_slug,
            stepTitle:  row.step_title,
          });
        }
      })();

      const ch = safeChannel(`rt:kid_step_narr:${kidUserId}:${s.sessionId}`)
        .on("postgres_changes",
          {
            event:  "*",
            schema: "public",
            table:  "cook_session_steps",
            filter: `cook_session_id=eq.${s.sessionId}`,
          },
          (payload) => {
            const row = payload?.new || payload?.old;
            if (!row || !row.step_id) return;
            const key = `${s.sessionId}::${row.step_id || row.id}`;
            // Narrate only on step OPEN (started_at set, finished_at
            // null). UPDATEs that flip finished_at don't re-fire.
            const isOpen = !!row.started_at && !row.finished_at;
            if (!isOpen) return;
            if (announcedRef.current.has(key)) return;
            announcedRef.current.add(key);
            narrateKidStep({
              foodSlug:   s.foodSlug,
              foodName:   s.foodName,
              targetKind: s.targetKind,
              petSlug:    pet.pet_slug,
              stepTitle:  row.step_title,
            });
          })
        .subscribe();
      channelsRef.current.set(s.sessionId, ch);
    }
  }, [rows, kidUserId, pet?.pet_slug]);

  // Cleanup on unmount.
  useEffect(() => {
    const map = channelsRef.current;
    return () => {
      for (const ch of map.values()) {
        try { supabase.removeChannel(ch); } catch { /* noop */ }
      }
      map.clear();
    };
  }, []);
}
